import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoadingController, NavController, PopoverController } from '@ionic/angular';
import _ from 'lodash';

import { Conversation, InboundEmail, InboundEmailRule } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { ConversationsService } from 'src/services/conversations.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';

import { ActionDropdownComponent, ActionDropdownType } from '../components/action-dropdown/action-dropdown.component';
import { CONSUMER_EMAIL_DOMAINS, CONVERSATION_STATUS } from '../constants';
import { downloadSlackAttachment, getEmailDomain } from '../utils/utils';

@Component({
  selector: 'app-display-inbound-email',
  templateUrl: './display-inbound-email.component.html',
  styleUrls: ['./display-inbound-email.component.scss'],
})
export class DisplayInboundEmailComponent implements OnInit {
  isLoading = false;

  inboundEmailId: string;
  inboundEmail: InboundEmail;
  inboundEmailRule: InboundEmailRule;
  conversation: Conversation;
  downloadFileId?: string;
  blockLabel: string;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private conversationsService: ConversationsService,
    private msgService: MessageService,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private loadingCtrl: LoadingController,
  ) {}

  async ngOnInit() {
    this.inboundEmailId = this.route.snapshot.paramMap.get('inboundEmailId');
    this.downloadFileId = this.route.snapshot.queryParamMap.get('downloadFileId');

    if (this.downloadFileId) {
      await downloadSlackAttachment(this.apiService, this.downloadFileId, undefined, this.conversation.id);
    }

    await this.fetchInboundEmail();
  }

  async fetchInboundEmail() {
    const inboundEmail: any = await this.apiService.getPromise(`/inbound-emails/${this.inboundEmailId}`);

    this.inboundEmail = inboundEmail;
    this.conversation = inboundEmail.inbound?.slackMessage?.ticket;

    this.fetchInboundEmailRule();
  }

  async fetchInboundEmailRule() {
    if (!this.inboundEmail.inboundEmailRuleId) {
      return;
    }

    try {
      this.inboundEmailRule = await this.inboundEmailRulesService.getById(this.inboundEmail.inboundEmailRuleId);

      if (!this.inboundEmailRule.blocklist) {
        this.inboundEmailRule.blocklist = [];
      }

      this.checkIfEmailBlocked(this.inboundEmailRule.blocklist, this.inboundEmail.fromUser.email);
    } catch (e) {
      this.msgService.showError(e);
    }
  }

  checkIfEmailBlocked(blocklist: string[], email: string) {
    const lowercaseEmail = email.toLowerCase();
    const domain = getEmailDomain(lowercaseEmail);

    for (const entry of blocklist) {
      const blockedEmailOrDomain = entry.toLowerCase();

      // Handle input as full email or just the domain
      if (blockedEmailOrDomain.includes('@') && blockedEmailOrDomain === lowercaseEmail) {
        this.blockLabel = 'Blocked';
        return;
      }
      if (blockedEmailOrDomain === domain) {
        this.blockLabel = 'Domain blocked';
        return;
      }
    }

    this.blockLabel = undefined;
  }

  async downloadAttachment(attachmentId: string) {
    return downloadSlackAttachment(this.apiService, attachmentId, undefined, this.conversation.id);
  }

  async showOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Mark not a ticket and close',
        icon: 'cloud-offline-outline',
      },
      {
        label: 'Block emails from this user',
        icon: 'person-remove-outline',
      },
    ];

    const domain = this.inboundEmail.fromUser.email.split('@')[1];
    if (!CONSUMER_EMAIL_DOMAINS.includes(domain.toLowerCase())) {
      options.push({
        label: 'Block all emails from this domain',
        icon: 'globe-outline',
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();
          if (idx === 0) {
            this.markNotTicket();
          } else if (idx === 1) {
            this.block(this.inboundEmail.fromUser.email);
          } else if (idx === 2) {
            this.block(getEmailDomain(this.inboundEmail.fromUser.email));
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async markNotTicket() {
    const loader = await this.loadingCtrl.create({
      message: 'Updating...',
      spinner: 'crescent',
    });

    loader.present();

    try {
      await this.conversationsService.update(this.conversation.id, {
        status: CONVERSATION_STATUS.CLOSED,
        excludeAnalytics: true,
        lockedAt: new Date(),
      });
      loader.dismiss();
    } catch (e) {
      this.msgService.showError(e);
      loader.dismiss();
    }
  }

  async block(emailOrDomain: string) {
    const loader = await this.loadingCtrl.create({
      message: 'Blocking...',
      spinner: 'crescent',
    });

    loader.present();

    const blocklist = this.inboundEmailRule.blocklist || [];

    blocklist.push(emailOrDomain);

    try {
      await this.inboundEmailRulesService.updatePartial(this.inboundEmailRule.id, { blocklist });

      // Try to close the conversation, but don't fail if validation prevents it
      try {
        await this.conversationsService.update(this.conversation.id, {
          status: CONVERSATION_STATUS.CLOSED,
          excludeAnalytics: true,
        });

        this.blockLabel = 'Blocked';
        this.msgService.show('Email blocked and conversation closed');
      } catch (closeError) {
        // Email was blocked successfully, but conversation couldn't be closed
        this.blockLabel = 'Blocked';
        this.msgService.show('Email blocked successfully. Please close this conversation manually.');
      }

      loader.dismiss();
    } catch (e) {
      this.msgService.showError(e);
      loader.dismiss();
    }
  }

  dismiss() {
    if (this.conversation) {
      this.navController.navigateRoot(`dashboard/inbox/${this.conversation.id}`);
    } else {
      this.navController.pop();
    }
  }
}
